import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { shiftLabel, lineEmoji } from "@/lib/ui";
import { ChevronLeft, ChevronRight, Camera, RotateCcw, RotateCw, FileText } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PhotoFilters, matchesFilters, weekDays, allocLabel, EMPTY } from "@/components/PhotoFilters";
import { kg as kgFmt } from "@/lib/calc";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-xl bg-slate-50 px-2.5 py-1.5"><div className="text-[10px] uppercase tracking-wide text-slate-400">{label}</div><div className="font-num font-bold text-slate-800 text-sm" data-testid={testid}>{value}</div></div>
);

function Thumb({ path, caption, onOpen }) {
  const [url, setUrl] = useState(null);
  const isPdf = (path || "").toLowerCase().endsWith(".pdf");
  useEffect(() => {
    let live = true; let objUrl = null;
    api.get(`/files/${path}`, { responseType: "blob" }).then((r) => { if (live) { objUrl = URL.createObjectURL(r.data); setUrl(objUrl); } }).catch(() => {});
    return () => { live = false; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [path]);
  if (isPdf) {
    return (
      <div>
        <button data-testid="gallery-thumb-pdf" onClick={() => url && window.open(url, "_blank")}
          className="w-full aspect-square rounded-2xl overflow-hidden border border-rose-200 bg-rose-50 flex flex-col items-center justify-center active:scale-95 transition-transform">
          <FileText className="text-rose-500" size={26} strokeWidth={2} />
          <span className="text-[10px] font-bold text-rose-600 mt-1">PDF</span>
        </button>
        {caption && <div className="text-[11px] text-slate-500 mt-1 line-clamp-2" data-testid="gallery-caption">{caption}</div>}
      </div>
    );
  }
  return (
    <div>
      <button data-testid="gallery-thumb" onClick={() => url && onOpen(url)}
        className="w-full aspect-square rounded-2xl overflow-hidden border border-slate-200 bg-slate-100 active:scale-95 transition-transform">
        {url ? <img src={url} alt="evidence" className="w-full h-full object-cover" /> : <div className="w-full h-full animate-pulse" />}
      </button>
      {caption && <div className="text-[11px] text-slate-500 mt-1 line-clamp-2" data-testid="gallery-caption">{caption}</div>}
    </div>
  );
}

export default function Photos() {
  const [entries, setEntries] = useState([]);
  const [week, setWeek] = useState(null);
  const [full, setFull] = useState(null);
  const [rot, setRot] = useState(0);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState(null);
  const [products, setProducts] = useState([]);
  const [flt, setFlt] = useState(EMPTY);

  const load = async (wk) => {
    setLoading(true);
    setFlt((f) => ({ ...EMPTY, q: f.q }));
    try {
      const r = await api.get("/kpi", { params: wk != null ? { week: wk } : {} });
      const w = wk != null ? wk : r.data.week;
      setWeek(w);
      const e = await api.get("/entries", { params: { week: w } });
      setEntries(e.data);
    } catch { setEntries([]); }
    setLoading(false);
  };
  useEffect(() => {
    load();
    api.get("/meta").then((r) => setMeta(r.data)).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pmap = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);
  const shots = entries.filter((e) => (e.photos || []).length);
  const shown = shots.filter((e) => matchesFilters(e, flt, pmap));

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-4xl mx-auto">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="font-heading font-extrabold text-3xl tracking-tight text-slate-800">Photos</h1>
          <p className="text-slate-500 text-sm">Scale &amp; count-sheet evidence, per shift</p>
        </div>
        <div className="flex items-center gap-1 bg-white rounded-full border border-slate-200 shadow-sm">
          <button data-testid="photos-week-prev" onClick={() => load(week - 1)} className="p-2.5 text-teal-600"><ChevronLeft size={18} strokeWidth={2.5} /></button>
          <span className="font-num font-semibold text-slate-800 px-1 text-sm whitespace-nowrap">Week {week ?? "…"}</span>
          <button data-testid="photos-week-next" onClick={() => load(week + 1)} className="p-2.5 text-teal-600"><ChevronRight size={18} strokeWidth={2.5} /></button>
        </div>
      </div>

      {loading && <div className="text-slate-400 text-sm">Loading photos…</div>}

      {!loading && shots.length > 0 && <PhotoFilters shots={shots} days={weekDays(meta?.week_anchor, week)} pmap={pmap} f={flt} set={setFlt} shown={shown} />}

      {!loading && shots.length > 0 && shown.length === 0 && (
        <div data-testid="photos-no-match" className="text-slate-400 bg-white border border-slate-100 rounded-2xl p-8 text-center text-sm">No photos match these filters.</div>
      )}

      {!loading && shots.length === 0 && (
        <div data-testid="photos-empty" className="text-slate-400 bg-white border border-slate-100 rounded-2xl p-10 text-center">
          <Camera className="mx-auto mb-2 text-slate-300" size={28} strokeWidth={2} />
          No photos filed for Week {week}. Supervisors can attach a photo on the "Photo of the scales" step when filing.
        </div>
      )}

      {!loading && shown.map((e) => (
        <div key={e.id} data-testid={`photo-shift-${e.id}`} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xl">{lineEmoji(e.line)}</span>
              <div className="min-w-0">
                <div className="font-heading font-bold text-slate-800 truncate">{e.line} · {shiftLabel(e.shift)}</div>
                <div className="text-[11px] text-slate-400">{e.date} · Logged by <span className="font-medium text-slate-600">{e.saved_by_name || "—"}</span>{e.filed_for_name ? <> for <span className="font-medium text-indigo-600">{e.filed_for_name}</span></> : null}</div>
              </div>
            </div>
            <span className="text-[11px] font-semibold text-teal-700 bg-teal-50 rounded-full px-2.5 py-1 whitespace-nowrap">{e.photos.length} 📸</span>
          </div>
          <div className="grid grid-cols-3 gap-2 mb-3" data-testid={`photo-stats-${e.id}`}>
            <Stat label="Crew earned" value={`${kgFmt(e.total_worked)} kg`} testid={`photo-kg-${e.id}`} />
            <Stat label="Given a home" value={`${kgFmt(e.total_allocated)} kg`} />
            <Stat label="Workers" value={(e.workers || []).length} testid={`photo-workers-${e.id}`} />
          </div>
          {(e.allocations || []).length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3" data-testid={`photo-products-${e.id}`}>
              {e.allocations.map((a, i) => <span key={i} className="text-[11px] rounded-full bg-slate-100 text-slate-600 px-2 py-0.5">{allocLabel(a, pmap)} · <b className="font-num">{kgFmt(a.kg)} kg</b></span>)}
            </div>
          )}
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
            {e.photos.map((p, i) => <Thumb key={i} path={p.storage_path} caption={p.caption} onOpen={(u) => { setFull(u); setRot(0); }} />)}
          </div>
        </div>
      ))}

      <Dialog open={!!full} onOpenChange={(o) => { if (!o) { setFull(null); setRot(0); } }}>
        <DialogContent className="max-w-3xl p-2 bg-black/90 border-0">
          <DialogHeader><DialogTitle className="sr-only">Photo evidence</DialogTitle></DialogHeader>
          {full && (
            <div className="relative flex items-center justify-center min-h-[60vh] py-4">
              <img data-testid="gallery-full" src={full} alt="evidence" style={{ transform: `rotate(${rot}deg)`, transition: "transform 0.2s ease" }} className="max-h-[70vh] max-w-full object-contain rounded-lg" />
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-3 z-50">
                <button data-testid="rotate-left" onClick={() => setRot((r) => r - 90)} className="flex items-center gap-1.5 bg-white/95 hover:bg-white text-slate-800 rounded-full px-4 h-11 shadow-lg active:scale-95 transition-transform font-semibold text-sm"><RotateCcw size={18} strokeWidth={2.5} /> Rotate</button>
                <button data-testid="rotate-right" onClick={() => setRot((r) => r + 90)} className="flex items-center gap-1.5 bg-white/95 hover:bg-white text-slate-800 rounded-full px-4 h-11 shadow-lg active:scale-95 transition-transform font-semibold text-sm">Rotate <RotateCw size={18} strokeWidth={2.5} /></button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
